// ==========================================
// FILE: src/components/features/Search/LocationPicker.js
// Fix 2: Modal now always renders above the Leaflet map.
//   - Overlay uses style={{ zIndex: 99999 }} instead of Tailwind z-50
//     because Leaflet internally creates stacking contexts that beat z-50
//   - Added useEffect to inject a one-time <style> block that caps Leaflet
//     tile-pane / marker-pane z-indices so they never exceed the modal
// ==========================================
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, MapPin } from 'lucide-react';
import { useAuth } from '../../../hooks/useAuth';

// Inject a stylesheet that prevents Leaflet panes from escaping the map container
function ensureLeafletZIndexCap() {
  if (document.getElementById('leaflet-z-cap')) return;
  const style = document.createElement('style');
  style.id = 'leaflet-z-cap';
  style.textContent = `
    /* Keep ALL Leaflet panes below the modal overlay (z: 99999) */
    .leaflet-pane         { z-index: 400 !important; }
    .leaflet-tile-pane    { z-index: 200 !important; }
    .leaflet-overlay-pane { z-index: 400 !important; }
    .leaflet-shadow-pane  { z-index: 500 !important; }
    .leaflet-marker-pane  { z-index: 600 !important; }
    .leaflet-tooltip-pane { z-index: 650 !important; }
    .leaflet-popup-pane   { z-index: 700 !important; }
    .leaflet-top,
    .leaflet-bottom       { z-index: 1000 !important; }
  `;
  document.head.appendChild(style);
}

const LocationPicker = ({ onLocationSelected, onClose }) => {
  const { user } = useAuth();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  const userCountry = user?.country || 'US';
  const userState = user?.state || '';

  const reverseGeocode = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=10`
      );
      const data = await response.json();
      if (data.address) {
        const { city, town, village, state, country, postcode } = data.address;
        const locality = city || town || village || 'Unknown';
        const zip = postcode ? `, ${postcode}` : '';
        return `${locality}, ${state || country}${zip}`;
      }
      return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
    } catch {
      return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
    }
  };

  const placeMarker = useCallback(async (lat, lng) => {
    if (!mapInstanceRef.current) return;
    if (markerRef.current) {
      mapInstanceRef.current.removeLayer(markerRef.current);
    }
    markerRef.current = window.L.marker([lat, lng]).addTo(mapInstanceRef.current);
    setIsLoading(true);
    const locationName = await reverseGeocode(lat, lng);
    setIsLoading(false);
    setSelectedLocation({ latitude: lat, longitude: lng, name: locationName });
  }, []);

  const createMap = useCallback((center) => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
    }
    const map = window.L.map(mapRef.current).setView(center, 13);
    mapInstanceRef.current = map;
    window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors'
    }).addTo(map);
    map.on('click', async (e) => {
      await placeMarker(e.latlng.lat, e.latlng.lng);
    });
    placeMarker(center[0], center[1]);
  }, [placeMarker]);

  const initMap = useCallback(() => {
    // Inject z-index cap BEFORE Leaflet renders anything
    ensureLeafletZIndexCap();

    if (!window.L || !mapRef.current) {
      if (!document.getElementById('leaflet-css')) {
        const cssLink = document.createElement('link');
        cssLink.id = 'leaflet-css';
        cssLink.rel = 'stylesheet';
        cssLink.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        document.head.appendChild(cssLink);
      }
      if (!document.getElementById('leaflet-js')) {
        const script = document.createElement('script');
        script.id = 'leaflet-js';
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.onload = () => initMap();
        document.body.appendChild(script);
      }
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => createMap([pos.coords.latitude, pos.coords.longitude]),
      ()    => createMap([40.7128, -74.0060])
    );
  }, [createMap]);

  useEffect(() => {
    initMap();
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [initMap]);

  const forwardGeocode = async (query) => {
    try {
      setIsSearching(true);
      let enhancedQuery = query;
      if (userState && !query.toLowerCase().includes(userState.toLowerCase())) {
        enhancedQuery += `, ${userState}`;
      }
      if (userCountry) enhancedQuery += `, ${userCountry}`;

      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(enhancedQuery)}&limit=1&countrycodes=${userCountry.toLowerCase()}`
      );
      const data = await response.json();
      setIsSearching(false);

      if (data && data.length > 0) {
        const latitude = parseFloat(data[0].lat);
        const longitude = parseFloat(data[0].lon);
        if (userCountry === 'US') {
          if (latitude < 24 || latitude > 50 || longitude > -66 || longitude < -125) {
            alert('Location is outside the United States. Please try a different search term.');
            return;
          }
        }
        if (mapInstanceRef.current) {
          mapInstanceRef.current.setView([latitude, longitude], 13);
        }
        await placeMarker(latitude, longitude);
      } else {
        alert(`Location not found. Please try a different search term.`);
      }
    } catch {
      setIsSearching(false);
      alert('Error searching for location. Please try again.');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) forwardGeocode(searchQuery.trim());
  };

  const handleConfirm = () => {
    if (selectedLocation) {
      onLocationSelected(selectedLocation);
      onClose();
    }
  };

  return (
    /*
     * Fix 2: Use inline style zIndex: 99999 so this portal sits above
     * Leaflet's internal stacking contexts (which can reach z ~1000).
     * Tailwind's z-50 = 50, which Leaflet beats.
     */
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 overflow-hidden"
      style={{ zIndex: 99999 }}
    >
      <div
        className="bg-white rounded-xl w-full max-w-2xl flex flex-col"
        style={{ maxHeight: '95vh', height: '95vh', zIndex: 100000 }}
      >
        {/* Header */}
        <div className="flex-shrink-0 p-4 border-b flex items-center justify-between bg-white rounded-t-xl">
          <div>
            <h3 className="text-lg sm:text-xl font-bold">Select Location</h3>
            {userState && (
              <p className="text-xs text-gray-600 mt-1">
                🌍 Searching in: {userState}, {userCountry}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 p-1 sm:p-2 hover:bg-gray-100 rounded-full transition"
            aria-label="Close"
          >
            <X size={24} />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-3 sm:p-4">
            <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-3">
              🔍 Search by city, state, or zip code, or tap anywhere on the map
            </p>

            {!userState && (
              <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3 mb-3">
                <p className="text-xs text-yellow-900 font-semibold">
                  ⚠️ Please set your state in your profile for better search results
                </p>
              </div>
            )}

            {/* Search form */}
            <form onSubmit={handleSearch} className="flex gap-2 mb-3 sm:mb-4">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={`Enter city or zip code${userState ? ` in ${userState}` : ''}`}
                className="flex-1 px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
              />
              <button
                type="submit"
                disabled={isSearching || !searchQuery.trim()}
                className="px-4 sm:px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition disabled:bg-gray-400 font-semibold text-sm sm:text-base whitespace-nowrap"
              >
                {isSearching ? '🔄' : 'Go'}
              </button>
            </form>

            {/* Map — rendered inside the modal, below the search bar */}
            <div
              ref={mapRef}
              className="w-full rounded-lg border-2 border-gray-200 mb-3 sm:mb-4"
              style={{ height: '40vh', minHeight: '250px', maxHeight: '400px' }}
            />

            {/* Selected location */}
            {selectedLocation && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-xs sm:text-sm font-semibold text-green-900">📍 Selected Location:</p>
                <p className="text-xs sm:text-sm text-green-700 font-medium mt-1">
                  {isLoading ? '🔄 Getting location name...' : selectedLocation.name}
                </p>
                <p className="text-xs text-green-600 mt-1">
                  Coordinates: {selectedLocation.latitude.toFixed(6)}, {selectedLocation.longitude.toFixed(6)}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex-shrink-0 p-3 sm:p-4 border-t bg-white rounded-b-xl">
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 sm:px-6 py-2 sm:py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-semibold text-sm sm:text-base"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={!selectedLocation || isLoading}
              className="flex-1 px-4 sm:px-6 py-2 sm:py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition disabled:bg-gray-400 font-semibold text-sm sm:text-base"
            >
              {isLoading ? 'Loading...' : 'Confirm Location'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationPicker;